/* eslint-disable */
import React, { useState, useEffect, useContext } from "react";
import { Sparkles, Zap, RefreshCw, Activity, Clock, CheckCircle2, RotateCcw, ShieldCheck, GitBranch, AlertTriangle, Layers } from "lucide-react";
import axios from "axios";
import { API_BASE } from "../config/apiConfig";
import { WorkspaceContext } from "../context/WorkspaceContext";

export default function TestEvolutionCenter() {
  const { activeRepoProject } = useContext(WorkspaceContext);

  const [metrics, setMetrics] = useState({
    active_tests: 12,
    outdated_tests: 3,
    under_review: 1,
    total_versions_managed: 18,
    evolution_events_logged: 8
  });

  const [outdatedQueue, setOutdatedQueue] = useState([]);
  const [loading, setLoading] = useState(false);

  // Evolution Modal States
  const [showEvolutionModal, setShowEvolutionModal] = useState(false);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [evolutionPrompt, setEvolutionPrompt] = useState(
    "In Release 2, the username field now allows email, username, and phone number. Expand this test with a data-driven matrix and update assertions."
  );
  const [targetRelease, setTargetRelease] = useState("Release 2");
  const [evolving, setEvolving] = useState(false);
  const [evolutionProposal, setEvolutionProposal] = useState(null);

  // Version History Modal
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);

  // Triage Simulation Modal
  const [showTriageModal, setShowTriageModal] = useState(false);
  const [triageData, setTriageData] = useState(null);
  const [triageLoading, setTriageLoading] = useState(false);

  // Status Notification
  const [bannerNotice, setBannerNotice] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, [activeRepoProject]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/v1/evolution/dashboard`);
      if (res.data && res.data.status === "success") {
        setMetrics(res.data.metrics || metrics);
        setOutdatedQueue(res.data.outdated_queue || []);
      }
    } catch (err) {
      console.error("Failed to load evolution dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEvolutionModal = (tc) => {
    setSelectedTestCase(tc);
    setEvolutionProposal(null);
    setShowEvolutionModal(true);
  };

  const handleProposeEvolution = async () => {
    if (!selectedTestCase) return;
    setEvolving(true);
    try {
      const tcId = selectedTestCase.tc_id || selectedTestCase.filename;
      const res = await axios.post(`${API_BASE}/api/v1/evolution/tests/${tcId}/propose-update`, {
        prompt: evolutionPrompt,
        target_release: targetRelease,
        project: activeRepoProject || "Demo Project"
      });
      if (res.data && res.data.status === "success") {
        setEvolutionProposal(res.data.proposal);
      }
    } catch (err) {
      alert("Failed to generate evolution proposal: " + (err.response?.data?.detail || err.message));
    } finally {
      setEvolving(false);
    }
  };

  const handleApproveEvolution = async () => {
    if (!selectedTestCase || !evolutionProposal) return;
    try {
      const tcId = selectedTestCase.tc_id || selectedTestCase.filename;
      const res = await axios.post(`${API_BASE}/api/v1/evolution/tests/${tcId}/approve-version`, {
        code: evolutionProposal.proposed_code,
        release_tag: targetRelease,
        data_matrix: evolutionProposal.data_matrix,
        change_classification: evolutionProposal.classification,
        change_reason: evolutionPrompt,
        author: "Lead QA Engineer",
        approved_by: "QA Signoff Lead"
      });
      if (res.data && res.data.status === "success") {
        setBannerNotice(`✅ Successfully evolved ${tcId} to Version v${res.data.result.version_number} (${targetRelease})!`);
        setShowEvolutionModal(false);
        fetchDashboard();
      }
    } catch (err) {
      alert("Failed to approve version: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleOpenHistory = async (tc) => {
    setSelectedTestCase(tc);
    setShowHistoryModal(true);
    try {
      const tcId = tc.tc_id || tc.filename;
      const res = await axios.get(`${API_BASE}/api/v1/evolution/tests/${tcId}/versions`);
      if (res.data && res.data.status === "success") {
        setVersionHistory(res.data.versions || []);
      }
    } catch (err) {
      console.error("Failed to load version history:", err);
    }
  };

  const handleRollback = async (targetVersion) => {
    if (!selectedTestCase) return;
    if (!window.confirm(`Are you sure you want to rollback to historical version v${targetVersion}?`)) return;
    try {
      const tcId = selectedTestCase.tc_id || selectedTestCase.filename;
      const res = await axios.post(`${API_BASE}/api/v1/evolution/tests/${tcId}/rollback`, {
        target_version: targetVersion,
        user_email: "Lead QA Engineer"
      });
      if (res.data && res.data.status === "success") {
        alert(`Rollback successful! Created new forward version v${res.data.result.version_number}.`);
        handleOpenHistory(selectedTestCase);
        fetchDashboard();
      }
    } catch (err) {
      alert("Rollback failed: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleSimulateTriage = async (tc) => {
    setSelectedTestCase(tc);
    setShowTriageModal(true);
    setTriageLoading(true);
    try {
      const tcId = tc.tc_id || tc.filename;
      const res = await axios.post(`${API_BASE}/api/v1/evolution/triage-failure`, {
        run_id: "RUN-20260722-0001",
        tc_id: tcId
      });
      if (res.data && res.data.status === "success") {
        setTriageData(res.data.triage);
      }
    } catch (err) {
      console.error("Triage failed:", err);
    } finally {
      setTriageLoading(false);
    }
  };

  const handleRunImpactAnalysis = async () => {
    try {
      const res = await axios.post(`${API_BASE}/api/v1/evolution/impact-analysis`, {
        project: activeRepoProject || "Demo Project",
        source_release: "Release 1",
        target_release: "Release 2",
        spec_diff: "LOGIN-REQ-001: Support email, username, and phone number authentication."
      });
      if (res.data && res.data.status === "success") {
        setBannerNotice(`⚡ Impact Analysis Complete: ${res.data.analysis.summary.tests_impacted} tests flagged as OUTDATED for Release 2 review.`);
        fetchDashboard();
      }
    } catch (err) {
      alert("Impact Analysis error: " + err.message);
    }
  };

  const defaultOutdatedList = [
    { tc_id: "TC-101", name: "Login Identifier Validation", current_version: 1, release_tag: "Release 1", status: "OUTDATED" },
    { tc_id: "TC-102", name: "Negative Credential Validation", current_version: 1, release_tag: "Release 1", status: "OUTDATED" },
    { tc_id: "TC-105", name: "User Session Profile Hydration", current_version: 1, release_tag: "Release 1", status: "ACTIVE" }
  ];

  const displayQueue = outdatedQueue.length > 0 ? outdatedQueue : defaultOutdatedList;

  return (
    <div style={{ padding: "28px 32px 48px", color: "#0f172a", backgroundColor: "#f8fafc", minHeight: "100vh", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      {/* 1. HERO HEADER BANNER */}
      <div style={{
        background: "#ffffff",
        border: "1px solid rgba(226, 232, 240, 0.9)",
        borderRadius: "20px",
        padding: "24px 28px",
        marginBottom: "24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "16px",
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 20px -2px rgba(0,0,0,0.04)"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div style={{
            background: "#FD6101",
            color: "#ffffff",
            width: "52px",
            height: "52px",
            borderRadius: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "26px",
            boxShadow: "0 8px 18px -4px rgba(253, 97, 1, 0.35)",
            flexShrink: 0
          }}>
            🧠
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <h1 style={{ margin: 0, fontSize: "24px", fontWeight: "900", color: "#0f172a", letterSpacing: "-0.4px" }}>
                Qintra Test Evolution Center
              </h1>
              <span style={{
                background: "#FD6101",
                color: "#ffffff",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "11px",
                fontWeight: "800",
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                v2.0 Enterprise
              </span>
            </div>
            <p style={{ color: "#64748b", fontSize: "13px", margin: "5px 0 0 0", fontWeight: "500" }}>
              Maintain autonomous alignment across software releases without masking genuine application defects.
            </p>
          </div>
        </div>

        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <button
            onClick={handleRunImpactAnalysis}
            style={{
              background: "#FD6101",
              color: "#fff",
              border: "none",
              padding: "11px 20px",
              borderRadius: "12px",
              fontWeight: "700",
              fontSize: "13px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              boxShadow: "0 4px 14px rgba(253, 97, 1, 0.25)",
              transition: "background 0.2s ease"
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#e05300"}
            onMouseLeave={(e) => e.currentTarget.style.background = "#FD6101"}
          >
              boxShadow: "0 4px 14px rgba(124, 58, 237, 0.25)",
              transition: "all 0.2s"
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Zap size={14} /> Run Release Impact Analysis</span>
          </button>
        </div>
      </div>

      {bannerNotice && (
        <div style={{ padding: "12px 18px", backgroundColor: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: "12px", marginBottom: "22px", fontSize: "13px", color: "#059669", display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: "600" }}>
          <span>{bannerNotice}</span>
          <button onClick={() => setBannerNotice(null)} style={{ background: "none", border: "none", color: "#059669", cursor: "pointer", fontWeight: "800" }}>✕</button>
        </div>
      )}

      {/* 2. KPI METRICS CARDS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginBottom: "24px" }}>
        <div style={{ backgroundColor: "#ffffff", padding: "18px 20px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 16px rgba(0,0,0,0.03)" }}>
          <div style={{ color: "#64748b", fontSize: "11.5px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Active Test Suite</div>
          <div style={{ fontSize: "28px", fontWeight: "900", marginTop: "4px", color: "#0284c7" }}>{metrics.active_tests || 12}</div>
          <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "4px" }}>Synchronized test specifications</div>
        </div>

        <div style={{ backgroundColor: "#ffffff", padding: "18px 20px", borderRadius: "16px", border: "1.5px solid #fed7aa", boxShadow: "0 4px 16px rgba(253,97,1,0.06)" }}>
          <div style={{ color: "#ea580c", fontSize: "11.5px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Outdated Tests</div>
          <div style={{ fontSize: "28px", fontWeight: "900", marginTop: "4px", color: "#FD6101" }}>{displayQueue.filter(t => t.status === "OUTDATED").length || 3}</div>
          <div style={{ fontSize: "11.5px", color: "#ea580c", fontWeight: "600", marginTop: "4px" }}>Release 2 diff pending alignment</div>
        </div>

        <div style={{ backgroundColor: "#ffffff", padding: "18px 20px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 16px rgba(0,0,0,0.03)" }}>
          <div style={{ color: "#64748b", fontSize: "11.5px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Under Review</div>
          <div style={{ fontSize: "28px", fontWeight: "900", marginTop: "4px", color: "#7c3aed" }}>{metrics.under_review || 1}</div>
          <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "4px" }}>Awaiting QA lead signoff</div>
        </div>

        <div style={{ backgroundColor: "#ffffff", padding: "18px 20px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 16px rgba(0,0,0,0.03)" }}>
          <div style={{ color: "#64748b", fontSize: "11.5px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Versions Managed</div>
          <div style={{ fontSize: "28px", fontWeight: "900", marginTop: "4px", color: "#059669" }}>{metrics.total_versions_managed || 18}</div>
          <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "4px" }}>Immutable cryptographic snapshots</div>
        </div>

        <div style={{ backgroundColor: "#ffffff", padding: "18px 20px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 16px rgba(0,0,0,0.03)" }}>
          <div style={{ color: "#64748b", fontSize: "11.5px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Audit Events</div>
          <div style={{ fontSize: "28px", fontWeight: "900", marginTop: "4px", color: "#e11d48" }}>{metrics.evolution_events_logged || 8}</div>
          <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "4px" }}>Tamper-proof ledger entries</div>
        </div>
      </div>

      {/* 3. EVOLUTION & OUTDATED QUEUE TABLE */}
      <div style={{ backgroundColor: "#ffffff", borderRadius: "18px", border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.03)", marginBottom: "24px" }}>
        <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#fafbfc" }}>
          <div>
            <h4 style={{ margin: 0, fontSize: "15px", fontWeight: "800", color: "#0f172a" }}>Evolution & Outdated Queue</h4>
            <span style={{ fontSize: "12px", color: "#64748b" }}>Tests flagged by Release Intelligence requiring requirement alignment</span>
          </div>
          <button
            onClick={fetchDashboard}
            style={{
              background: "#f1f5f9",
              border: "1px solid #e2e8f0",
              color: "#FD6101",
              padding: "6px 12px",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: "700",
              cursor: "pointer"
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}><RefreshCw size={13} /> Refresh Queue</span>
          </button>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", textAlign: "left" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #e2e8f0", color: "#64748b", backgroundColor: "#f8fafc" }}>
              <th style={{ padding: "14px 20px", fontWeight: "700" }}>Test ID</th>
              <th style={{ padding: "14px 20px", fontWeight: "700" }}>Test Name / Feature</th>
              <th style={{ padding: "14px 20px", fontWeight: "700" }}>Version</th>
              <th style={{ padding: "14px 20px", fontWeight: "700" }}>Release</th>
              <th style={{ padding: "14px 20px", fontWeight: "700" }}>Lifecycle Status</th>
              <th style={{ padding: "14px 20px", textAlign: "right", fontWeight: "700" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayQueue.map((tc, idx) => (
              <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.15s ease" }}>
                <td style={{ padding: "16px 20px", fontWeight: "700", color: "#7c3aed" }}>
                  {tc.tc_id || tc.filename}
                </td>
                <td style={{ padding: "16px 20px" }}>
                  <div style={{ fontWeight: "700", color: "#0f172a" }}>{tc.name || tc.filename}</div>
                  <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px" }}>Authentication & User Management</div>
                </td>
                <td style={{ padding: "16px 20px" }}>
                  <span style={{ backgroundColor: "#f1f5f9", color: "#334155", padding: "3px 8px", borderRadius: "6px", fontSize: "12px", fontWeight: "700" }}>
                    v{tc.current_version || 1}
                  </span>
                </td>
                <td style={{ padding: "16px 20px", color: "#64748b", fontWeight: "600" }}>
                  {tc.release_tag || "Release 1"}
                </td>
                <td style={{ padding: "16px 20px" }}>
                  <span style={{
                    padding: "4px 10px",
                    borderRadius: "20px",
                    fontSize: "11px",
                    fontWeight: "800",
                    backgroundColor: tc.status === "OUTDATED" ? "#fff7ed" : "#ecfdf5",
                    color: tc.status === "OUTDATED" ? "#ea580c" : "#059669",
                    border: tc.status === "OUTDATED" ? "1px solid #fed7aa" : "1px solid #a7f3d0"
                  }}>
                    ● {tc.status || "ACTIVE"}
                  </span>
                </td>
                <td style={{ padding: "16px 20px", textAlign: "right" }}>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                    <button
                      onClick={() => handleOpenEvolutionModal(tc)}
                      style={{ background: "#FD6101", color: "#fff", border: "none", padding: "7px 14px", borderRadius: "8px", fontSize: "12px", fontWeight: "700", cursor: "pointer", boxShadow: "0 2px 8px rgba(253, 97, 1, 0.2)", transition: "background 0.2s ease" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#e05300"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "#FD6101"}
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}><Sparkles size={13} /> Evolve with AI</span>
                    </button>
                    <button
                      onClick={() => handleSimulateTriage(tc)}
                      style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fca5a5", padding: "7px 12px", borderRadius: "8px", fontSize: "12px", fontWeight: "700", cursor: "pointer" }}
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}><Activity size={13} /> Failure Triage</span>
                    </button>
                    <button
                      onClick={() => handleOpenHistory(tc)}
                      style={{ background: "#f8fafc", color: "#475569", border: "1px solid #cbd5e1", padding: "7px 12px", borderRadius: "8px", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}
                    >
                      🕒 History
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 4. AUTONOMOUS EVOLUTION INTELLIGENCE PIPELINE */}
      <div style={{ backgroundColor: "#ffffff", borderRadius: "18px", border: "1px solid #e2e8f0", padding: "24px 28px", marginBottom: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.03)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div>
            <h4 style={{ margin: 0, fontSize: "15px", fontWeight: "800", color: "#0f172a" }}>
              ⚡ Autonomous Release Alignment Pipeline
            </h4>
            <span style={{ fontSize: "12px", color: "#64748b" }}>Continuous synchronization between requirements, PR spec diffs, and executable test suites</span>
          </div>
          <span style={{ fontSize: "11px", fontWeight: "800", color: "#059669", background: "#d1fae5", padding: "4px 10px", borderRadius: "14px" }}>
            Pipeline Active & Watching
          </span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "14px" }}>
          {[
            { step: "01", title: "SCM & Spec Diff", desc: "Monitors Git PRs & OpenAPI schema changes", color: "#0284c7" },
            { step: "02", title: "Semantic Analysis", desc: "Detects genuine requirement evolution vs breaking bug", color: "#FD6101" },
            { step: "03", title: "Matrix Expansion", desc: "Auto-generates parametric data-driven matrices", color: "#7c3aed" },
            { step: "04", title: "Flaky Test Isolation", desc: "Distinguishes timing jitter from real regressions", color: "#e11d48" },
            { step: "05", title: "Version Sign-Off", desc: "Commits immutable SHA-256 version snapshot", color: "#059669" }
          ].map((item, idx) => (
            <div key={idx} style={{ padding: "16px", borderRadius: "12px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "11px", fontWeight: "900", color: item.color, marginBottom: "4px" }}>STEP {item.step}</div>
              <div style={{ fontSize: "13.5px", fontWeight: "800", color: "#0f172a", marginBottom: "4px" }}>{item.title}</div>
              <div style={{ fontSize: "11.5px", color: "#64748b", lineHeight: "1.4" }}>{item.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 5. DEFECT MASKING GUARDRAILS & AUDIT TRAIL */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
        <div style={{ backgroundColor: "#ffffff", borderRadius: "18px", border: "1px solid #e2e8f0", padding: "22px 26px", boxShadow: "0 4px 20px rgba(0,0,0,0.03)" }}>
          <h4 style={{ margin: "0 0 14px 0", fontSize: "14px", fontWeight: "800", color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}><ShieldCheck size={18} color="#059669" /> Zero Defect Masking Governance Mandate</span>
          </h4>
          <p style={{ fontSize: "12.5px", color: "#64748b", margin: "0 0 16px 0", lineHeight: "1.5" }}>
            Qintra AI strictly enforces mathematical divergence guards. If an application change returns an unhandled HTTP 500 or broken assertion without a corresponding requirement update, the test is classified as a <strong>Genuine Defect</strong> and blocked from automatic healing.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <div style={{ padding: "10px 14px", borderRadius: "8px", background: "#f8fafc", border: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "12px", fontWeight: "700", color: "#334155" }}>Immutable Rollback Engine</span>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#059669" }}>Active</span>
            </div>
            <div style={{ padding: "10px 14px", borderRadius: "8px", background: "#f8fafc", border: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "12px", fontWeight: "700", color: "#334155" }}>Cryptographic Version Ledger</span>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#059669" }}>SHA-256 Verified</span>
            </div>
            <div style={{ padding: "10px 14px", borderRadius: "8px", background: "#f8fafc", border: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "12px", fontWeight: "700", color: "#334155" }}>QA Lead Dual-Signature Mandate</span>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#7c3aed" }}>Enforced</span>
            </div>
          </div>
        </div>

        <div style={{ backgroundColor: "#ffffff", borderRadius: "18px", border: "1px solid #e2e8f0", padding: "22px 26px", boxShadow: "0 4px 20px rgba(0,0,0,0.03)" }}>
          <h4 style={{ margin: "0 0 14px 0", fontSize: "14px", fontWeight: "800", color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}><Clock size={18} color="#7c3aed" /> Recent Evolution Audit Events</span>
          </h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {[
              { id: "TC-101", action: "Evolved to v2", by: "AI Copilot (Approved by Lead QA)", time: "10m ago", tag: "Release 2 Matrix" },
              { id: "TC-104", action: "Failure Triage (Defect Logged)", by: "Triaged as Bug #492", time: "1h ago", tag: "Defect Guarded" },
              { id: "TC-108", action: "Version Rollback", by: "Reverted to v2 by QA Lead", time: "3h ago", tag: "Rollback" }
            ].map((ev, i) => (
              <div key={i} style={{ padding: "12px 14px", borderRadius: "10px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "12.5px", fontWeight: "800", color: "#0f172a" }}>
                    {ev.id} • {ev.action}
                  </div>
                  <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
                    {ev.by} • {ev.time}
                  </div>
                </div>
                <span style={{ fontSize: "10.5px", fontWeight: "800", padding: "3px 8px", borderRadius: "6px", backgroundColor: "#f1f5f9", color: "#475569" }}>
                  {ev.tag}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* MODAL 1: EVOLVE WITH AI */}
      {showEvolutionModal && selectedTestCase && (
        <div className="modal-overlay" style={{ zIndex: 10005, background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)" }}>
          <div className="modal-card" style={{ backgroundColor: "#ffffff", maxWidth: "860px", width: "100%", maxHeight: "calc(100vh - 60px)", borderRadius: "18px", border: "1px solid #e2e8f0", padding: "26px", overflowY: "auto", color: "#0f172a", boxShadow: "0 20px 40px rgba(0,0,0,0.12)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0", paddingBottom: "14px", marginBottom: "18px" }}>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "800" }}>
                Evolve Test: <span style={{ color: "#7c3aed" }}>{selectedTestCase.tc_id || selectedTestCase.filename}</span>
              </h3>
              <button onClick={() => setShowEvolutionModal(false)} style={{ background: "none", border: "none", color: "#64748b", fontSize: "18px", cursor: "pointer" }}>✕</button>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontSize: "12px", color: "#64748b", marginBottom: "6px", fontWeight: "700" }}>Target Release</label>
              <select
                value={targetRelease}
                onChange={(e) => setTargetRelease(e.target.value)}
                style={{ width: "100%", padding: "10px 14px", backgroundColor: "#f8fafc", border: "1.5px solid #cbd5e1", borderRadius: "10px", color: "#0f172a", fontSize: "13px" }}
              >
                <option value="Release 2">Release 2.0 (Active SCM / Sprint)</option>
                <option value="Release 3">Release 3.0 (Next Release)</option>
              </select>
            </div>

            <div style={{ marginBottom: "18px" }}>
              <label style={{ display: "block", fontSize: "12px", color: "#64748b", marginBottom: "6px", fontWeight: "700" }}>Requirement Change Description / Prompt</label>
              <textarea
                value={evolutionPrompt}
                onChange={(e) => setEvolutionPrompt(e.target.value)}
                rows={3}
                style={{ width: "100%", padding: "12px", backgroundColor: "#f8fafc", border: "1.5px solid #cbd5e1", borderRadius: "10px", color: "#0f172a", fontSize: "13px", resize: "vertical", boxSizing: "border-box" }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginBottom: "20px" }}>
              <button
                onClick={handleProposeEvolution}
                disabled={evolving}
                style={{ background: "#FD6101", color: "#fff", border: "none", padding: "10px 20px", borderRadius: "10px", fontWeight: "700", cursor: "pointer", boxShadow: "0 4px 12px rgba(253, 97, 1, 0.25)", transition: "background 0.2s ease" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#e05300"}
                onMouseLeave={(e) => e.currentTarget.style.background = "#FD6101"}
              >
                {evolving ? "⚙️ Analyzing & Generating Refactor..." : "🚀 Generate Evolved Version (AI Diff)"}
              </button>
            </div>

            {evolutionProposal && (
              <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "18px" }}>
                <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "14px" }}>
                  <span style={{ backgroundColor: "#ecfdf5", color: "#059669", border: "1px solid #a7f3d0", padding: "4px 10px", borderRadius: "8px", fontSize: "12px", fontWeight: "700" }}>
                    Classification: {evolutionProposal.classification}
                  </span>
                  <span style={{ fontSize: "12px", color: "#64748b" }}>
                    Confidence: {(evolutionProposal.confidence * 100).toFixed(0)}% • Version: v{evolutionProposal.from_version} → v{evolutionProposal.to_version}
                  </span>
                </div>

                {/* Data Matrix Preview */}
                {evolutionProposal.data_matrix && evolutionProposal.data_matrix.length > 0 && (
                  <div style={{ marginBottom: "16px", backgroundColor: "#f8fafc", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a", marginBottom: "8px" }}>
                      📊 Evolved Parameter Matrix (Data-Driven Testing)
                    </div>
                    <table style={{ width: "100%", fontSize: "12px", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ color: "#64748b", textAlign: "left", borderBottom: "1px solid #e2e8f0" }}>
                          <th style={{ padding: "6px" }}>Identifier</th>
                          <th style={{ padding: "6px" }}>Type</th>
                          <th style={{ padding: "6px" }}>Expected Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {evolutionProposal.data_matrix.map((row, rIdx) => (
                          <tr key={rIdx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                            <td style={{ padding: "6px", fontFamily: "monospace", fontWeight: "600" }}>{row.identifier}</td>
                            <td style={{ padding: "6px", color: "#7c3aed" }}>{row.type}</td>
                            <td style={{ padding: "6px" }}>
                              <span style={{ color: row.expected_pass ? "#059669" : "#dc2626", fontWeight: "700" }}>
                                {row.expected_pass ? "PASS (Allowed)" : "FAIL (Validation Error)"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Proposed Code Preview */}
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ fontSize: "12px", fontWeight: "700", color: "#64748b", marginBottom: "6px" }}>
                    Refactored Playwright Python Script
                  </div>
                  <pre style={{ backgroundColor: "#0f172a", padding: "14px", borderRadius: "10px", fontSize: "12px", color: "#38bdf8", overflowX: "auto", maxHeight: "250px", fontFamily: "monospace" }}>
                    {evolutionProposal.proposed_code}
                  </pre>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                  <button onClick={() => setShowEvolutionModal(false)} style={{ background: "#ffffff", color: "#64748b", border: "1px solid #cbd5e1", padding: "10px 18px", borderRadius: "10px", cursor: "pointer", fontWeight: "600" }}>
                    Cancel
                  </button>
                  <button onClick={handleApproveEvolution} style={{ background: "#10b981", color: "#fff", border: "none", padding: "10px 22px", borderRadius: "10px", fontWeight: "700", cursor: "pointer", transition: "background 0.2s ease" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#059669"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "#10b981"}
                  >
                    ✓ Approve & Commit Version v{evolutionProposal.to_version}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL 2: VERSION HISTORY */}
      {showHistoryModal && selectedTestCase && (
        <div className="modal-overlay" style={{ zIndex: 10005, background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)" }}>
          <div className="modal-card" style={{ backgroundColor: "#ffffff", maxWidth: "780px", width: "100%", maxHeight: "calc(100vh - 60px)", borderRadius: "18px", border: "1px solid #e2e8f0", padding: "26px", overflowY: "auto", color: "#0f172a", boxShadow: "0 20px 40px rgba(0,0,0,0.12)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0", paddingBottom: "14px", marginBottom: "18px" }}>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "800" }}>
                🕒 Immutable Versions: <span style={{ color: "#7c3aed" }}>{selectedTestCase.tc_id || selectedTestCase.filename}</span>
              </h3>
              <button onClick={() => setShowHistoryModal(false)} style={{ background: "none", border: "none", color: "#64748b", fontSize: "18px", cursor: "pointer" }}>✕</button>
            </div>

            {versionHistory.length === 0 ? (
              <p style={{ color: "#64748b", padding: "20px", textAlign: "center" }}>No historical version snapshots found.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {versionHistory.map((v, vIdx) => (
                  <div key={vIdx} style={{ backgroundColor: "#f8fafc", padding: "14px 18px", borderRadius: "10px", border: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{ backgroundColor: "#7c3aed", color: "#fff", padding: "2px 8px", borderRadius: "6px", fontSize: "12px", fontWeight: "700" }}>
                          v{v.version_number}
                        </span>
                        <span style={{ fontWeight: "700", fontSize: "14px" }}>{v.release_tag}</span>
                        <span style={{ fontSize: "11.5px", color: "#64748b" }}>({v.change_classification})</span>
                      </div>
                      <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>{v.change_reason || "Standard release update"}</div>
                      <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>Created on {v.created_at} by {v.author}</div>
                    </div>

                    <button
                      onClick={() => handleRollback(v.version_number)}
                      style={{ background: "#ffffff", border: "1px solid #cbd5e1", color: "#FD6101", padding: "6px 14px", borderRadius: "8px", fontSize: "12px", fontWeight: "700", cursor: "pointer" }}
                    >
                      ↺ Rollback
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL 3: FAILURE TRIAGE */}
      {showTriageModal && selectedTestCase && (
        <div className="modal-overlay" style={{ zIndex: 10005, background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)" }}>
          <div className="modal-card" style={{ backgroundColor: "#ffffff", maxWidth: "800px", width: "100%", maxHeight: "calc(100vh - 60px)", borderRadius: "18px", border: "1px solid #e2e8f0", padding: "26px", overflowY: "auto", color: "#0f172a", boxShadow: "0 20px 40px rgba(0,0,0,0.12)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0", paddingBottom: "14px", marginBottom: "18px" }}>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "800" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}><Activity size={13} /> Failure Triage</span> Diagnostics: <span style={{ color: "#dc2626" }}>{selectedTestCase.tc_id || selectedTestCase.filename}</span>
              </h3>
              <button onClick={() => setShowTriageModal(false)} style={{ background: "none", border: "none", color: "#64748b", fontSize: "18px", cursor: "pointer" }}>✕</button>
            </div>

            {triageLoading ? (
              <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
                ⚡ Correlating execution traces against requirements and recent Git diffs...
              </div>
            ) : triageData ? (
              <div>
                <div style={{ padding: "14px", borderRadius: "10px", backgroundColor: triageData.is_defect ? "#fef2f2" : "#fff7ed", border: `1px solid ${triageData.is_defect ? "#fca5a5" : "#fed7aa"}`, marginBottom: "16px" }}>
                  <div style={{ fontWeight: "800", color: triageData.is_defect ? "#dc2626" : "#ea580c", fontSize: "14px" }}>
                    Diagnosis: {triageData.verdict}
                  </div>
                  <div style={{ fontSize: "12.5px", color: "#334155", marginTop: "4px" }}>
                    {triageData.explanation}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
                  <div style={{ backgroundColor: "#f8fafc", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: "11px", fontWeight: "700", color: "#64748b" }}>ROOT CAUSE</div>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: "#0f172a", marginTop: "3px" }}>{triageData.root_cause}</div>
                  </div>
                  <div style={{ backgroundColor: "#f8fafc", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: "11px", fontWeight: "700", color: "#64748b" }}>CONFIDENCE</div>
                    <div style={{ fontSize: "13px", fontWeight: "800", color: "#059669", marginTop: "3px" }}>{(triageData.confidence * 100).toFixed(0)}% Certain</div>
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                  <button onClick={() => setShowTriageModal(false)} style={{ background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", padding: "9px 18px", borderRadius: "8px", cursor: "pointer", fontWeight: "600" }}>
                    Close
                  </button>
                  {!triageData.is_defect && (
                    <button
                      onClick={() => {
                        setShowTriageModal(false);
                        handleOpenEvolutionModal(selectedTestCase);
                      }}
                      style={{ background: "#FD6101", color: "#fff", border: "none", padding: "9px 20px", borderRadius: "8px", fontWeight: "700", cursor: "pointer", transition: "background 0.2s ease" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#e05300"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "#FD6101"}
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Sparkles size={13} /> Evolve to Match Release</span>
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <p style={{ color: "#64748b", padding: "20px", textAlign: "center" }}>No triage report generated.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
